import {
  AccountToolbarSettings,
  type ActionButton,
  NavigationToolbarSettings,
  type PermissionDefinition,
} from '@bizzdesign/shared/models';
import { baseRoutes, type FeatureFlagKey } from '@bizzdesign/shared/utils';
import { isObject } from 'lodash-es';

export interface LayoutConfig {
  header: HeaderConfig;
  sidebar?: SidebarConfig;
  drawer: DrawerConfig;
  footer?: boolean;
  pageLayout: boolean;
}

export function isLayoutConfig(config: unknown): config is LayoutConfig {
  return (
    isObject(config) &&
    'header' in config &&
    isObject(config.header) &&
    'drawer' in config &&
    isObject(config.drawer) &&
    'pageLayout' in config &&
    typeof config.pageLayout === 'boolean'
  );
}

export type DrawerEffect = 'overlay' | 'push';
export interface DrawerConfig {
  animation?: boolean;
  effect: DrawerEffect;
}
export interface HeaderConfig {
  position?: 'fixed' | 'float';
  navigation?: NavigationToolbarSettings;
  dynamic?: boolean;
  account?: AccountToolbarSettings;
  showSearchbar?: boolean;
}

export type ToolbarButton = ActionButton;

export interface SidebarItem {
  name: string;
  routerLink: string;
  icon?: string;
  dataId: string;
  subItems?: SidebarItem[];
  permissions: PermissionDefinition;
  featureFlag?: FeatureFlagKey;
}

export interface SidebarConfig {
  selectedItem?: string;
  sidebarItems?: SidebarItem[];
}

export const defaultHeaderConfig: HeaderConfig = {
  dynamic: false,
  account: new AccountToolbarSettings({
    showAvatarGroups: false,
    showToolbarSecondaryButton: false,
    showToolbarPrimaryButton: true,
    showAccountNotifications: false,
    showTrialWidget: true,
  }),
};

export const defaultDrawerConfig: DrawerConfig = {
  effect: 'overlay',
  animation: true,
};

export const frameHeaderConfig: HeaderConfig = {
  position: 'float',
  navigation: new NavigationToolbarSettings({
    showMenu: true,
    showFilepath: true,
    showToolbarSecondaryButton: true,
    showToolbarPrimaryButton: true,
  }),
  dynamic: true,
  account: new AccountToolbarSettings({
    showAvatarGroups: true,
    showToolbarSecondaryButton: true,
    showAccountNotifications: false,
    showToolbarPrimaryButton: true,
    showTrialStatusButton: true,
  }),
};

const defaultSidebarConfig: SidebarConfig = {
  selectedItem: 'home',
  sidebarItems: [
    {
      name: 'home',
      routerLink: baseRoutes.home,
      dataId: 'home',
      icon: 'ui-house',
      permissions: {
        view: 'home',
      },
    },
    {
      name: 'workspaces',
      routerLink: baseRoutes.workspaces,
      dataId: 'workspaces',
      icon: 'ui-workspace',
      permissions: {
        manage: 'workspaces',
      },
    },
    {
      name: 'dataManagement',
      routerLink: baseRoutes.dataManagement,
      dataId: 'data-management',
      icon: 'ui-spreadsheet',
      permissions: {
        manage: 'dataManagement',
      },
      subItems: [
        {
          name: 'populations',
          routerLink: baseRoutes.populations,
          dataId: 'populations',
          permissions: {
            manage: 'populations',
          },
          featureFlag: 'populationsEnabled',
        },
        {
          name: 'manageAttributes',
          routerLink: baseRoutes.manageAttributes,
          dataId: 'manage-attributes',
          permissions: {
            manage: 'attributes',
          },
        },
        {
          name: 'dataImport',
          routerLink: baseRoutes.dataImport,
          dataId: 'data-import',
          permissions: {
            manage: 'dataImport',
          },
        },
      ],
    },
    {
      name: 'archive',
      routerLink: baseRoutes.archive,
      dataId: 'archive',
      icon: 'ui-archive',
      permissions: {
        manage: 'workspaces',
      },
    },
  ],
};

export const defaultLayoutConfig: LayoutConfig = {
  header: defaultHeaderConfig,
  sidebar: defaultSidebarConfig,
  drawer: defaultDrawerConfig,
  pageLayout: false,
};

export const errorPageLayoutConfig: LayoutConfig = {
  header: defaultHeaderConfig,
  sidebar: { ...defaultSidebarConfig, selectedItem: '' },
  drawer: defaultDrawerConfig,
  pageLayout: false,
};

export const searchLayoutConfig: LayoutConfig = {
  header: { ...defaultHeaderConfig, position: undefined },
  sidebar: { ...defaultSidebarConfig, selectedItem: '' },
  drawer: { ...defaultDrawerConfig, effect: 'push' },
  pageLayout: true,
};

export const workspaceHeaderConfig: HeaderConfig = {
  navigation: new NavigationToolbarSettings({
    showLogo: false,
    showMenu: false,
    showFilepath: false,
    showToolbarSecondaryButton: false,
  }),
  dynamic: false,
  account: new AccountToolbarSettings({
    showAvatarGroups: false,
    showToolbarSecondaryButton: false,
    showToolbarPrimaryButton: true,
    showAccountNotifications: false,
    showTrialWidget: true,
    showTrialInviteButton: false,
  }),
};
export const frameLayoutConfig: LayoutConfig = {
  header: frameHeaderConfig,
  drawer: defaultDrawerConfig,
  footer: true,
  pageLayout: false,
};

export const workspaceLayoutConfig: LayoutConfig = {
  header: workspaceHeaderConfig,
  sidebar: { ...defaultSidebarConfig, selectedItem: 'workspaces' },
  drawer: defaultDrawerConfig,
  pageLayout: true,
};

export const WorkspacesLayoutConfig: LayoutConfig = {
  header: { ...defaultHeaderConfig, showSearchbar: true },
  sidebar: { ...defaultSidebarConfig, selectedItem: 'workspaces' },
  drawer: defaultDrawerConfig,
  pageLayout: true,
};

export const ArchiveLayoutConfig: LayoutConfig = {
  header: { ...defaultHeaderConfig, showSearchbar: true },
  sidebar: { ...defaultSidebarConfig, selectedItem: 'archive' },
  drawer: defaultDrawerConfig,
  pageLayout: true,
};

export const dataManagementLayoutConfig: LayoutConfig = {
  header: defaultHeaderConfig,
  sidebar: { ...defaultSidebarConfig, selectedItem: 'dataManagement' },
  drawer: defaultDrawerConfig,
  pageLayout: true,
};

export const dataImportLayoutConfig: LayoutConfig = {
  header: defaultHeaderConfig,
  sidebar: {
    ...defaultSidebarConfig,
    selectedItem: 'dataImport',
  },
  drawer: defaultDrawerConfig,
  pageLayout: true,
};

export const manageAttributesLayoutConfig: LayoutConfig = {
  header: defaultHeaderConfig,
  sidebar: {
    ...defaultSidebarConfig,
    selectedItem: 'manageAttributes',
  },
  drawer: defaultDrawerConfig,
  pageLayout: true,
};

export const populationsLayoutConfig: LayoutConfig = {
  header: defaultHeaderConfig,
  sidebar: {
    ...defaultSidebarConfig,
    selectedItem: 'populations',
  },
  drawer: defaultDrawerConfig,
  pageLayout: true,
};
